import {useEffect, useState} from "react";
import {Alert, Checkbox, FormControlLabel} from "@mui/material";
import MainActions from "@/components/MainActions";
import VLFSelectNew from "@/components/Input/SelectNew";
import {CustomFootballImportAPI} from "@/api/customFootballImport";
const kinds = {player: "Giocatore", club: "Club", competition: "Competizione"};
const statuses = {
	new: "Nuova anagrafica",
	linked: "Collegamento già riconosciuto",
	selected: "Collegamento da confermare",
	unresolved: "Corrispondenza da scegliere",
};
const label = (p) => p.name + (p.birthDate ? " · " + p.birthDate : "");
function IdentityChoice({row, choice, createNew, onChange, busy}) {
	const [term, setTerm] = useState(""),
		[matches, setMatches] = useState([]),
		[loading, setLoading] = useState(false),
		[error, setError] = useState("");
	useEffect(() => {
		let active = true;
		setMatches([]);
		setError("");
		setLoading(false);
		if (term.trim().length < 2) return;
		const timer = setTimeout(async () => {
			setLoading(true);
			try {
				const data = await CustomFootballImportAPI.searchIdentities(
					row.kind,
					term.trim(),
				);
				if (active) setMatches(data || []);
			} catch {
				if (active)
					setError("Ricerca non riuscita. Riprova modificando il testo.");
			} finally {
				if (active) setLoading(false);
			}
		}, 350);
		return () => {
			active = false;
			clearTimeout(timer);
		};
	}, [term, row.kind]);
	const candidates = [
		...new Map(
			[
				...(row.candidates || []),
				...(row.matchedEntity ? [row.matchedEntity] : []),
				...matches,
			].map((p) => [p.id, p]),
		).values(),
	];
	const selected = choice !== undefined ? choice : row.entityId;
	const selectedPlayer = candidates.find((p) => p.id === selected);
	return (
		<div className="border rounded p-3 mb-3" style={{overflowWrap: "anywhere"}}>
			<strong>
				{kinds[row.kind]}: {row.name}
			</strong>
			<p className="mb-1">
				{statuses[row.status]} · ID sorgente: {row.externalId}
			</p>
			{row.matchedEntity && (
				<p>Anagrafica collegata: {label(row.matchedEntity)}</p>
			)}
			{row.status !== "linked" && (
				<>
					<VLFSelectNew
						label="Collega a un’anagrafica esistente"
						aria-label={"Collega " + row.name}
						options={candidates.map((p) => ({value: p.id, label: label(p)}))}
						value={
							selected
								? {
										value: selected,
										label: selectedPlayer ? label(selectedPlayer) : row.name,
									}
								: null
						}
						onChange={(v) => onChange(v?.value || null, false)}
						onInputChange={(text, meta) => {
							if (meta.action === "input-change") setTerm(text);
						}}
						placeholder="Cerca per nome o scegli una proposta"
						noOptionsMessage={() =>
							term.length < 2
								? "Scrivi almeno due caratteri"
								: "Nessuna corrispondenza"
						}
						isLoading={loading}
						isDisabled={busy || createNew}
					/>
					{!!row.candidateCount && (
						<p>
							{row.candidateCount} corrispondenze proposte. Puoi cercare altre
							anagrafiche per nome.
						</p>
					)}
					{row.kind === "player" && (
						<FormControlLabel
							control={
								<Checkbox
									checked={createNew}
									disabled={busy}
									onChange={(e) => onChange(null, e.target.checked)}
								/>
							}
							label="Crea una persona distinta, anche in presenza di omonimi"
						/>
					)}
					{error && <Alert severity="error">{error}</Alert>}
				</>
			)}
		</div>
	);
}
export default function ImportIdentityReview({
	review,
	selections,
	newPlayers,
	onChange,
	acknowledge,
	onAcknowledge,
	busy,
}) {
	const [conflictPage, setConflictPage] = useState(0);
	const [page, setPage] = useState(0),
		[onlyUnresolved, setOnlyUnresolved] = useState(false);
	useEffect(() => {
		setPage(0);
		setConflictPage(0);
	}, [review, onlyUnresolved]);
	const all = review.identities || [],
		unresolved = all.filter((r) => r.status === "unresolved");
	const rows = onlyUnresolved ? unresolved : all;
	const start = Math.min(
		page * 25,
		Math.max(0, Math.floor((rows.length - 1) / 25) * 25),
	);
	return (
		<section className="mt-3" aria-label="Verifica delle anagrafiche">
			<h4>Corrispondenze anagrafiche</h4>
			<p>
				{all.length} identità controllate · {unresolved.length} da risolvere ·{" "}
				{(review.conflicts || []).length} differenze nei dati.
			</p>
			<p>
				I collegamenti confermati verranno ricordati per gli import successivi.
				I dati anagrafici presenti saranno conservati.
			</p>
			{!!unresolved.length && (
				<Alert severity="warning">
					Scegli le anagrafiche da riutilizzare; per un giocatore omonimo puoi
					confermare una persona distinta. Poi ripeti la verifica.
				</Alert>
			)}
			<FormControlLabel
				control={
					<Checkbox
						checked={onlyUnresolved}
						onChange={(e) => setOnlyUnresolved(e.target.checked)}
						disabled={busy}
					/>
				}
				label="Mostra solo le corrispondenze da risolvere"
			/>
			{rows.slice(start, start + 25).map((row) => (
				<IdentityChoice
					key={row.kind + ":" + row.externalId}
					row={row}
					choice={selections[row.kind]?.[row.externalId]}
					createNew={
						newPlayers.includes(row.externalId) && row.kind === "player"
					}
					onChange={(id, fresh) =>
						onChange(row.kind, row.externalId, id, fresh)
					}
					busy={busy}
				/>
			))}
			{rows.length > 25 && (
				<>
					<p>
						{start + 1}–{Math.min(start + 25, rows.length)} di {rows.length}
					</p>
					<MainActions
						actions={[
							{
								label: "Precedenti",
								disabled: busy || start === 0,
								onClick: () => setPage(Math.max(0, page - 1)),
							},
							{
								label: "Successive",
								disabled: busy || start + 25 >= rows.length,
								onClick: () => setPage(page + 1),
							},
						]}
					/>
				</>
			)}
			{!!review.conflicts?.length && (
				<Alert severity="warning">
					<strong>Differenze rilevate</strong>
					{review.conflicts
						.slice(conflictPage * 25, conflictPage * 25 + 25)
						.map((c, i) => (
							<p key={i}>
								{kinds[c.kind]} {c.externalId} ·{" "}
								{{
									firstName: "Nome",
									lastName: "Cognome",
									birthDate: "Data di nascita",
									name: "Nome",
								}[c.field] || c.field}
								: presente «{c.current || "non indicato"}», nel file «
								{c.incoming || "non indicato"}».
							</p>
						))}
					{review.conflicts.length > 25 && (
						<>
							<p>
								Differenze {conflictPage * 25 + 1}–
								{Math.min(conflictPage * 25 + 25, review.conflicts.length)} di{" "}
								{review.conflicts.length}
							</p>
							<MainActions
								actions={[
									{
										label: "Differenze precedenti",
										disabled: busy || conflictPage === 0,
										onClick: () => setConflictPage(conflictPage - 1),
									},
									{
										label: "Differenze successive",
										disabled:
											busy ||
											(conflictPage + 1) * 25 >= review.conflicts.length,
										onClick: () => setConflictPage(conflictPage + 1),
									},
								]}
							/>
						</>
					)}
					<FormControlLabel
						control={
							<Checkbox
								checked={acknowledge}
								disabled={busy}
								onChange={(e) => onAcknowledge(e.target.checked)}
							/>
						}
						label="Ho verificato le differenze: conserva i dati anagrafici già presenti"
					/>
				</Alert>
			)}
		</section>
	);
}
